import { useState, useEffect, useMemo, memo } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Chip,
    Stack,
    Tooltip,
    Grid,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import {
    Description as DescriptionIcon,
    Edit as EditIcon,
    EventAvailable as EventAvailableIcon,
    EventNote as ScheduleIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    FilterAlt as FilterIcon,
    FilterAltOff as FilterOffIcon,
    Flight as FlightIcon,
    Link as LinkIcon,
    List as ListIcon,
    MoreVert as MoreVertIcon,
    Sync as ChangeIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { themeConstants } from '@/theme';
import { formatDate } from '@/utils/helpers';
import {
    useAddAbsentMutation,
    useRemoveAbsentMutation,
    useGetUserInfoQuery,
    useGetResourcesSettingsQuery,
    useGetAgendaQuery,
    useGetSettingsQuery,
} from '@/store/apiSlice';
import { useAppDispatch, useCategoriesResources } from '@/store/hooks';
import {
    setAbsentUsers,
    setAgenda,
    setFiles,
    setScheduleDate,
    setChangeRequests,
} from '@/store/modalSlice';
import { open, openEventModal } from '@/store/uiSlice';
import CategoryItem from '@/components/CategoryItem';
import CategoryItemCT from '@/components/CategoryItemChurchTools.tsx';
import ResponsiveButton from '@/components/ResponsiveButton';
import type { EventData } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';

interface EventProps {
    data: EventData;
    isHighlighted?: boolean;
    autoCollapse?: boolean;
}

const Event = ({ data, isHighlighted = false, autoCollapse = false }: EventProps) => {
    const {
        date,
        event,
        location,
        startTime,
        endTime,
        categories: assignedCategories,
        absent = {},
        excludedCategories = [],
        hasScheduleItems = false,
        canceled = false,
    } = data;

    const dispatch = useAppDispatch();
    const { data: userInfo } = useGetUserInfoQuery();
    const { data: settings } = useGetSettingsQuery();
    const { data: resourcesSettings } = useGetResourcesSettingsQuery();
    const { LL } = useI18nContext();
    const { alert } = useAppDialog();

    const imAbsent = userInfo ? absent.hasOwnProperty(userInfo.userId) : false;
    const canEditEvent = !!(userInfo?.admin || userInfo?.eventEditor);

    // If event is canceled -> collapse by default and mark as canceled
    const shouldExpand =
        !imAbsent && !canceled && (isHighlighted || (userInfo?.autoExpandEvents && !autoCollapse));

    const [expanded, setExpanded] = useState(shouldExpand);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [showAllCategories, setShowAllCategories] = useState(false);

    // Update expanded state when user preferences or highlight status changes
    useEffect(() => {
        setExpanded(shouldExpand);
    }, [shouldExpand]);

    const { data: agendaData } = useGetAgendaQuery(
        { from: date, limit: 1 },
        { skip: !date || !settings?.churchtools?.enableAgenda }
    );

    const agendaForDate = agendaData?.[0];
    const agendaItems = agendaForDate?.agenda || [];
    const services = agendaForDate?.services || [];
    const files = agendaForDate?.files || [];

    // Get transformed categories
    const categories = useCategoriesResources();

    const [addAbsent, { isLoading: isAddingAbsent }] = useAddAbsentMutation();
    const [removeAbsent, { isLoading: isRemovingAbsent }] = useRemoveAbsentMutation();

    const isAbsentLoading = isAddingAbsent || isRemovingAbsent;

    // Check if user is assigned to any category
    const isAssignedToCategory = useMemo(() => {
        if (!userInfo || !assignedCategories) return false;
        return Object.values(assignedCategories).some((assigned) => {
            const users = assigned.users ?? [];
            return users.some((u) => u.id === userInfo.userId);
        });
    }, [userInfo, assignedCategories]);

    // Calculate visible categories from resourcesSettings
    const visibleCategories = useMemo(() => {
        if (!resourcesSettings || !userInfo) {
            return [];
        }

        // If showing all categories, return all category names
        if (showAllCategories) {
            return [
                ...categories.map((cat) => cat.name),
                ...services.map((service) => service.name),
            ];
        }

        // Get all category names (including ChurchTools services)
        const allCategoryNames = [
            ...categories.map((cat) => cat.name),
            ...services.map((service) => service.name),
        ];

        return allCategoryNames.filter((name) => {
            // Always show categories where user is assigned
            const category = categories.find((cat) => cat.name === name);
            if (category) {
                const isUserAssigned = category.resources?.some(
                    (resource) => resource.id === userInfo.userId
                );
                if (isUserAssigned) {
                    return true; // Always visible if user is assigned
                }
            }

            // Otherwise, show only if not hidden
            return !resourcesSettings.hiddenCategories.includes(name);
        });
    }, [resourcesSettings, categories, services, userInfo, showAllCategories]);

    // Check if user has any active filters (hidden categories)
    const hasActiveFilters = useMemo(() => {
        return resourcesSettings && resourcesSettings.hiddenCategories.length > 0;
    }, [resourcesSettings]);

    // Don't render until userInfo is available
    if (!userInfo) {
        return null;
    }

    const handleAbsentToggle = async () => {
        if (!userInfo) return;

        if (!imAbsent && isAssignedToCategory) {
            await alert(LL.event.absenceWarning());
            return;
        }

        try {
            if (imAbsent) {
                await removeAbsent({ date, resource: userInfo.userId }).unwrap();
                setExpanded(true);
            } else {
                await addAbsent({ date, resource: userInfo.userId }).unwrap();
                setExpanded(false);
            }
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleCopyLink = () => {
        const url = `${window.location.origin}${window.location.pathname}#d${date.replace(/-/g, '')}`;
        window.history.pushState({}, '', `#d${date.replace(/-/g, '')}`);
        if (navigator?.clipboard) {
            void navigator.clipboard.writeText(url);
        }
    };

    const handleShowAbsent = () => {
        const absentUsersList = Object.values(absent);
        if (absentUsersList.length > 0) {
            dispatch(setAbsentUsers(absentUsersList));
            dispatch(open('absent'));
        }
    };

    const handleShowAgenda = () => {
        if (agendaItems.length > 0) {
            dispatch(setAgenda(agendaItems));
            dispatch(open('agenda'));
        }
    };

    const handleShowFiles = () => {
        if (files.length > 0) {
            dispatch(setFiles(files));
            dispatch(open('files'));
        }
    };

    const handleEditEvent = () => {
        dispatch(
            openEventModal({
                event: event ?? '',
                date,
                location: location ?? '',
                endTime: endTime ?? '',
                startTime: startTime ?? '',
                excludedCategories: excludedCategories,
                eventId: data.eventId,
                canceled: !!canceled,
            })
        );
    };

    const weekday = new Date(date.replace(/-/g, '/')).toLocaleDateString(undefined, {
        weekday: 'long',
    });

    const timeLabel = useMemo(() => {
        const formatTime = (t?: string) => (t ?? '').split(':').slice(0, 2).join(':');
        const start = formatTime(startTime);
        const end = formatTime(endTime);

        if (!start && !end) return '';
        if (start && end) return `${start} - ${end}`;
        return start || end;
    }, [startTime, endTime]);

    const absentCount = Object.keys(absent).length;
    const isToday = new Date(date).toDateString() === new Date().toDateString();
    const isPast = new Date(date) < new Date(new Date().setHours(0, 0, 0, 0));

    // Compute open change requests (requested replacements) for this event.
    const openChangeRequests = useMemo(() => {
        if (!assignedCategories)
            return [] as { categoryName: string; userName: string; count: number }[];

        // requestedUsers is a list of userIds for which a replacement is requested.
        // We group by category + userId.
        const rows: { categoryId: number; userId: number; count: number }[] = [];

        Object.entries(assignedCategories).forEach(([categoryIdStr, assigned]) => {
            const categoryId = Number(categoryIdStr);
            const requested = assigned?.requestedUsers ?? [];
            if (!requested.length) return;

            requested.forEach((userId) => {
                const existing = rows.find(
                    (r) => r.categoryId === categoryId && r.userId === userId
                );
                if (existing) existing.count += 1;
                else rows.push({ categoryId, userId, count: 1 });
            });
        });

        const categoryNameById = new Map(categories.map((c) => [c.id, c.name] as const));

        return rows
            .map((r) => {
                const assigned = assignedCategories[r.categoryId];
                const userName =
                    (assigned?.users ?? []).find((u) => u.id === r.userId)?.name ??
                    String(r.userId);
                return {
                    categoryName: categoryNameById.get(r.categoryId) ?? String(r.categoryId),
                    userName,
                    count: r.count,
                };
            })
            .sort(
                (a, b) =>
                    a.categoryName.localeCompare(b.categoryName) ||
                    a.userName.localeCompare(b.userName)
            );
    }, [assignedCategories, categories]);

    const openChangeRequestCount = useMemo(
        () => openChangeRequests.reduce((acc, r) => acc + r.count, 0),
        [openChangeRequests]
    );

    const handleShowChangeRequests = () => {
        if (openChangeRequests.length === 0) return;
        dispatch(setChangeRequests(openChangeRequests));
        dispatch(open('changeRequests'));
    };

    return (
        <Paper
            id={`d${date.replace(/-/g, '')}`}
            elevation={isHighlighted ? 4 : 1}
            sx={{
                mb: 2,
                opacity: imAbsent ? 0.6 : 1,
                background: canceled
                    ? 'rgba(255,152,0,0.08)'
                    : isHighlighted
                      ? isPast
                          ? 'rgba(156,39,176,0.15)'
                          : 'rgba(59,130,246,0.15)'
                      : isPast
                        ? 'rgba(156,39,176,0.05)'
                        : 'transparent',
                border: isHighlighted ? '2px solid' : '1px solid',
                borderColor: isHighlighted
                    ? isPast
                        ? 'secondary.main'
                        : 'primary.main'
                    : 'divider',
                overflow: 'hidden',
                backdropFilter: themeConstants.effects.backdropBlur,
            }}
        >
            {/* Header Section - Always Visible */}
            <Box
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    background: canceled
                        ? 'rgba(255,152,0,0.08)'
                        : isHighlighted
                          ? isPast
                              ? 'rgba(156,39,176,0.15)'
                              : 'rgba(59,130,246,0.15)'
                          : isPast
                            ? 'rgba(156,39,176,0.05)'
                            : 'transparent',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack direction="row" sx={{ mb: 2, gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                            direction="row"
                            sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
                        >
                            <Stack spacing={0.1} sx={{ flex: 1 }}>
                                <Stack
                                    direction="row"
                                    sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
                                >
                                    <Typography
                                        variant="h6"
                                        component="h2"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                        }}
                                    >
                                        {formatDate(date)}
                                    </Typography>

                                    {isToday ? (
                                        <Chip
                                            icon={<EventAvailableIcon />}
                                            label={LL.event.today()}
                                            color="primary"
                                            variant="filled"
                                            size="small"
                                            sx={{ fontWeight: 'bold', height: 24 }}
                                        />
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                            {weekday}
                                        </Typography>
                                    )}
                                </Stack>

                                <Stack
                                    direction="row"
                                    sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
                                >
                                    {timeLabel && (
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                            }}
                                        >
                                            {timeLabel}
                                        </Typography>
                                    )}

                                    {event && (
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                                        >
                                            {event}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>

                            {openChangeRequestCount > 0 && (
                                <Chip
                                    icon={<ChangeIcon fontSize="small" />}
                                    label={`${openChangeRequestCount}`}
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                    sx={{ height: 24, mt: 1, alignSelf: 'start' }}
                                    onClick={handleShowChangeRequests}
                                />
                            )}

                            {absentCount > 0 && (
                                <Chip
                                    icon={<FlightIcon fontSize="small" />}
                                    label={`${absentCount}`}
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                    sx={{ height: 24, mt: 1, alignSelf: 'start' }}
                                    onClick={handleShowAbsent}
                                />
                            )}

                            {!!canceled && (
                                <Chip
                                    label={LL.eventModal.canceled()}
                                    color="warning"
                                    size="small"
                                    sx={{ fontWeight: 'bold', height: 24 }}
                                />
                            )}
                        </Stack>
                    </Box>

                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            backgroundColor: 'action.hover',
                        }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Stack>

                <Stack
                    direction="row"
                    sx={{
                        gap: { xs: 0.5, sm: 1 },
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    {/* Absent/Present Button */}
                    <Tooltip
                        title={!imAbsent && isAssignedToCategory ? LL.event.absenceWarning() : ''}
                        arrow
                    >
                        <ResponsiveButton
                            icon={
                                isAbsentLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <FlightIcon fontSize="small" />
                                )
                            }
                            label={imAbsent ? LL.event.present() : LL.event.absent()}
                            onClick={handleAbsentToggle}
                            disabled={isAbsentLoading || (!imAbsent && isAssignedToCategory)}
                            variant={imAbsent ? 'contained' : 'outlined'}
                            color={imAbsent ? 'warning' : 'inherit'}
                        />
                    </Tooltip>

                    {/* Agenda Button */}
                    {settings?.churchtools?.enableAgenda && agendaItems.length > 0 && (
                        <ResponsiveButton
                            icon={<ListIcon fontSize="small" />}
                            label={LL.event.showAgenda()}
                            onClick={handleShowAgenda}
                            color="secondary"
                            variant="contained"
                        />
                    )}

                    {/* Files Button */}
                    {settings?.churchtools?.enableFiles && files.length > 0 && (
                        <ResponsiveButton
                            icon={<DescriptionIcon fontSize="small" />}
                            label={LL.event.showFiles()}
                            onClick={handleShowFiles}
                            color="secondary"
                            variant="contained"
                        />
                    )}

                    {/* Schedule Button */}
                    {settings?.schedule?.enabled && (
                        <ResponsiveButton
                            icon={<ScheduleIcon fontSize="small" />}
                            label={LL.event.schedule()}
                            onClick={() => {
                                dispatch(setScheduleDate(date));
                                dispatch(open('schedule'));
                            }}
                            color={hasScheduleItems ? 'primary' : 'inherit'}
                            variant={hasScheduleItems ? 'contained' : 'outlined'}
                        />
                    )}

                    {/* Toggle Show All Categories Button */}
                    {hasActiveFilters && (
                        <ResponsiveButton
                            icon={
                                showAllCategories ? (
                                    <FilterIcon fontSize="small" />
                                ) : (
                                    <FilterOffIcon fontSize="small" />
                                )
                            }
                            label={
                                showAllCategories
                                    ? LL.event.activateFilter()
                                    : LL.event.deactivateFilter()
                            }
                            onClick={() => {
                                setShowAllCategories(!showAllCategories);
                                setExpanded(true);
                            }}
                            color={showAllCategories ? 'primary' : 'inherit'}
                            variant={showAllCategories ? 'contained' : 'outlined'}
                        />
                    )}

                    {/* Copy Link Button */}
                    <ResponsiveButton
                        icon={<LinkIcon fontSize="small" />}
                        label={LL.event.copyLink()}
                        onClick={handleCopyLink}
                    />

                    {/* Edit Event Button */}
                    {canEditEvent && (
                        <ResponsiveButton
                            icon={<EditIcon fontSize="small" />}
                            label={LL.event.edit()}
                            onClick={handleEditEvent}
                            color="error"
                        />
                    )}

                    <Box sx={{ flex: 1 }} />

                    {/* Burger Menu (Mobile only) */}
                    <IconButton
                        onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                        sx={{
                            display: 'inline-flex',
                            '@media (min-width:800px)': {
                                display: 'none',
                            },
                            border: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>

                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={() => setMenuAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem
                            onClick={() => {
                                void handleAbsentToggle();
                                setMenuAnchorEl(null);
                            }}
                            disabled={isAbsentLoading || (!imAbsent && isAssignedToCategory)}
                        >
                            <ListItemIcon>
                                {imAbsent ? (
                                    <FlightIcon sx={{ color: 'warning.main' }} />
                                ) : (
                                    <FlightIcon />
                                )}
                            </ListItemIcon>
                            <ListItemText>
                                {imAbsent ? LL.event.present() : LL.event.absent()}
                            </ListItemText>
                        </MenuItem>

                        {openChangeRequestCount > 0 && (
                            <MenuItem
                                onClick={() => {
                                    handleShowChangeRequests();
                                    setMenuAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <ChangeIcon sx={{ color: 'warning.main' }} />
                                </ListItemIcon>
                                <ListItemText>{LL.changeRequests.title()}</ListItemText>
                            </MenuItem>
                        )}

                        {settings?.churchtools?.enableAgenda && agendaItems.length > 0 && (
                            <MenuItem
                                onClick={() => {
                                    handleShowAgenda();
                                    setMenuAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <ListIcon sx={{ color: 'secondary.main' }} />
                                </ListItemIcon>
                                <ListItemText>{LL.event.showAgenda()}</ListItemText>
                            </MenuItem>
                        )}

                        {settings?.churchtools?.enableFiles && files.length > 0 && (
                            <MenuItem
                                onClick={() => {
                                    handleShowFiles();
                                    setMenuAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon sx={{ color: 'secondary.main' }} />
                                </ListItemIcon>
                                <ListItemText>{LL.event.showFiles()}</ListItemText>
                            </MenuItem>
                        )}

                        {settings?.schedule?.enabled && (
                            <MenuItem
                                onClick={() => {
                                    dispatch(setScheduleDate(date));
                                    dispatch(open('schedule'));
                                    setMenuAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <ScheduleIcon
                                        sx={{
                                            color: hasScheduleItems ? 'primary.main' : 'inherit',
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText>{LL.event.schedule()}</ListItemText>
                            </MenuItem>
                        )}

                        {hasActiveFilters && (
                            <MenuItem
                                onClick={() => {
                                    setShowAllCategories(!showAllCategories);
                                    setMenuAnchorEl(null);
                                    setExpanded(true);
                                }}
                            >
                                <ListItemIcon>
                                    {showAllCategories ? (
                                        <FilterIcon sx={{ color: 'primary.main' }} />
                                    ) : (
                                        <FilterOffIcon />
                                    )}
                                </ListItemIcon>
                                <ListItemText>
                                    {showAllCategories
                                        ? LL.event.activateFilter()
                                        : LL.event.deactivateFilter()}
                                </ListItemText>
                            </MenuItem>
                        )}

                        <MenuItem
                            onClick={() => {
                                handleCopyLink();
                                setMenuAnchorEl(null);
                            }}
                        >
                            <ListItemIcon>
                                <LinkIcon />
                            </ListItemIcon>
                            <ListItemText>{LL.event.copyLink()}</ListItemText>
                        </MenuItem>

                        {canEditEvent && (
                            <>
                                <Divider />
                                <MenuItem
                                    onClick={() => {
                                        handleEditEvent();
                                        setMenuAnchorEl(null);
                                    }}
                                >
                                    <ListItemIcon>
                                        <EditIcon sx={{ color: 'error.main' }} />
                                    </ListItemIcon>
                                    <ListItemText>{LL.event.edit()}</ListItemText>
                                </MenuItem>
                            </>
                        )}
                    </Menu>
                </Stack>
            </Box>

            <Collapse in={expanded} timeout="auto">
                <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                    <Grid container spacing={1}>
                        {(() => {
                            const filteredCategories = categories.filter(
                                (cat) =>
                                    cat.active === 1 &&
                                    visibleCategories.includes(cat.name) &&
                                    !excludedCategories.includes(cat.id)
                            );

                            if (filteredCategories.length === 0) {
                                return (
                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                border: '2px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                bgcolor: 'rgba(59,130,246,0.05)',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 1 }}
                                            >
                                                {LL.filterModal.emptyCategoriesHintShort()}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            }

                            return filteredCategories.flatMap((category) => {
                                const assigned = assignedCategories?.[category.id];
                                const allowMultiple = Boolean(category.allowMultiple);

                                if (!allowMultiple) {
                                    return (
                                        <Grid
                                            size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}
                                            key={category.id}
                                        >
                                            <CategoryItem
                                                category={category}
                                                date={date}
                                                assigned={assigned}
                                                absent={absent}
                                                eventName={event}
                                                eventLocation={location}
                                                eventStartTime={startTime}
                                                eventEndTime={endTime}
                                            />
                                        </Grid>
                                    );
                                }

                                const minAssignments = category.minAssignments ?? 1;
                                const maxAssignments = category.maxAssignments ?? 1;

                                const users = assigned?.users ?? [];
                                const filledCount = users.length;

                                // Base slots: always show assigned users, and at least the minimum required slots.
                                const minSlots = Math.min(minAssignments, maxAssignments);
                                const baseSlots = Math.max(filledCount, minSlots);

                                // Optional hint slot: show one extra empty slot only if there's room below max.
                                // Show it whenever there is remaining capacity below max.
                                // (Min slots already communicate the requirement; the extra slot communicates that
                                // additional/optional assignments are possible.)
                                const shouldShowOptionalSlot =
                                    filledCount >= minAssignments && filledCount < maxAssignments;

                                const slotsToRender = Math.min(
                                    baseSlots + (shouldShowOptionalSlot ? 1 : 0),
                                    maxAssignments
                                );

                                return Array.from({ length: slotsToRender }).map((_, slotIndex) => {
                                    const userForSlot = users[slotIndex];

                                    return (
                                        <Grid
                                            size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}
                                            key={`${category.id}-${slotIndex}`}
                                        >
                                            <CategoryItem
                                                category={category}
                                                date={date}
                                                assigned={assigned}
                                                user={userForSlot}
                                                absent={absent}
                                                eventName={event}
                                                eventLocation={location}
                                                eventStartTime={startTime}
                                                eventEndTime={endTime}
                                            />
                                        </Grid>
                                    );
                                });
                            });
                        })()}
                        {settings?.churchtools?.enableCategories &&
                            services
                                .slice()
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .filter((service) => visibleCategories.includes(service.name))
                                .map(
                                    (service, i) =>
                                        service.responsible && (
                                            <Grid
                                                size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}
                                                key={i}
                                            >
                                                <CategoryItemCT
                                                    name={service.name}
                                                    responsible={service.responsible}
                                                    agreed={service.agreed}
                                                />
                                            </Grid>
                                        )
                                )}
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default memo(Event);
